import { TASK_STATUSES, TASK_STATUS_PARAM, TASK_UNASSIGNED_VALUE } from '../constants';
import TasksFilters from './TasksFilters';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@fluentui/react-components';
import { AddFilled } from '@fluentui/react-icons';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { getReorderNeighbors } from '@shared/helpers/reorderNeighbors';
import { PROJECT_DETAILS_MAX_WIDTH_PX } from '../../constants';
import type { TProject } from '../../types';
import { CustomScrollbar } from '@shared/components/CustomScrollbar';
import { ConfirmDialog, type TConfirmDialogState } from '@shared/components/dialogs/ConfirmDialog';
import type { ETaskStatus, TTaskListItem } from '../types';
import TaskFormDialog from './TaskFormDialog';
import { StatusLabel } from '@shared/components/StatusLabel';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { useTasks, useCreateTask, useArchiveTask, useReorderTasks } from '../api';
import TasksTable from './TasksTable';
import { StageDialog, StagesTimeline, getStageTableSections, useProjectStages } from '@modules/mocks/projectStages';

type TProps = {
  project: TProject;
  rootItemId: string;
  statusPending: boolean;
  onStatusChange: (task: TTaskListItem, status: ETaskStatus) => void;
};

const ProjectTasks = ({ project, rootItemId, statusPending, onStatusChange }: TProps) => {
  const projectId = project._id;
  // Mock: stages and gates from organization.mock.projectStages; a project without stages keeps the flat list.
  const { stages } = useProjectStages(projectId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const createTask = useCreateTask(projectId);
  const { searchParams, change, changeMany } = useSearchParams();
  const archive = useArchiveTask();
  const reorder = useReorderTasks();
  const [archiveDialogState, setArchiveDialogState] = useState<TConfirmDialogState>();
  const onArchive = (task: TTaskListItem) => {
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: `Archive ${TERMINOLOGY.task.lower}?`,
      content: `"${task.title}" will be moved to the archive.`,
      buttonLabel: 'Archive',
      danger: true,
      onConfirm: async () => {
        try {
          await archive.mutateAsync(task._id);
          setArchiveDialogState({ ...dialogState, isOpen: false });
        } catch {
          /* The mutation shows the error; keep the confirmation open for retry. */
        }
      },
    };
    setArchiveDialogState(dialogState);
  };
  const search = searchParams.get('search') ?? '';
  // The backend trims the search, so only spaces filter nothing.
  const searchFilter = search.trim();
  const statusValues = searchParams.get(TASK_STATUS_PARAM)?.split(',') ?? [];
  const statuses = TASK_STATUSES.filter((option) => statusValues.includes(option.value)).map((option) => option.value);
  // Like the statuses, keep only values the filter can show: a link may name someone no longer in the Project.
  const assigneeValues = [...project.leads, ...project.members, TASK_UNASSIGNED_VALUE];
  const assignee =
    searchParams
      .get('assignee')
      ?.split(',')
      .filter((id) => assigneeValues.includes(id)) ?? [];
  const { data, error, isFetching, dataUpdatedAt, refetch } = useTasks({
    project: projectId,
    search: searchFilter,
    status: statuses,
    assignee,
  });
  // A failed refresh blocks reordering. Refetch once per loaded list so it recovers without a reload, never in a loop.
  const refetchedAfterLoadAt = useRef<number>();
  useEffect(() => {
    if (!error || !data || refetchedAfterLoadAt.current === dataUpdatedAt) return;
    refetchedAfterLoadAt.current = dataUpdatedAt;
    void refetch();
  }, [error, data, dataUpdatedAt, refetch]);
  const reorderDisabledReason =
    searchFilter || statuses.length || assignee.length
      ? `Clear all filters to reorder ${TERMINOLOGY.task.lowerPlural}.`
      : error
        ? `${TERMINOLOGY.task.upperPlural} could not be refreshed. Try again.`
        : reorder.isPending || isFetching
          ? `Wait for ${TERMINOLOGY.task.lowerPlural} to finish updating.`
          : '';
  const onReorder = (sourceId: string, targetId: string) => {
    if (!data) return;
    const neighbors = getReorderNeighbors(
      data.items.map((task) => task._id),
      sourceId,
      targetId
    );
    if (!neighbors) return;
    reorder.mutate({
      project: projectId,
      taskId: sourceId,
      expectedRevision: data.taskOrderRevision,
      previousTaskId: neighbors.previousId,
      nextTaskId: neighbors.nextId,
    });
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {stages.length > 0 && (
        <div className="mx-auto w-full shrink-0 pl-5 pr-8" style={{ maxWidth: PROJECT_DETAILS_MAX_WIDTH_PX }}>
          <StagesTimeline projectId={projectId} stages={stages} />
        </div>
      )}
      {data?.permissions.canCreate && (
        <div className="mx-auto flex w-full shrink-0 pl-5 pr-8" style={{ maxWidth: PROJECT_DETAILS_MAX_WIDTH_PX }}>
          <Button appearance="primary" icon={<AddFilled />} onClick={() => setIsCreateOpen(true)}>
            New
          </Button>
        </div>
      )}
      <div className="mx-auto w-full shrink-0 pl-5 pr-8" style={{ maxWidth: PROJECT_DETAILS_MAX_WIDTH_PX }}>
        <TasksFilters
          project={project}
          search={search}
          statuses={statuses}
          assignee={assignee}
          onAssigneeChange={(value) => change('assignee', value.length ? value.join(',') : undefined)}
          onSearchChange={(value) => change('search', value || undefined)}
          onStatusesChange={(values) => change(TASK_STATUS_PARAM, values.length ? values.join(',') : undefined)}
          onClear={() => changeMany({ search: undefined, [TASK_STATUS_PARAM]: undefined, assignee: undefined })}
        />
      </div>
      <CustomScrollbar className="min-h-0 flex-1">
        <div className="mx-auto w-full pl-5 pr-8 pb-6" style={{ maxWidth: PROJECT_DETAILS_MAX_WIDTH_PX }}>
          <StatusLabel
            data={data}
            error={error}
            errorMessage={`Loading ${TERMINOLOGY.task.lowerPlural} failed!`}
            loadingMessage={`Loading ${TERMINOLOGY.task.lowerPlural}...`}
            render={(result) =>
              result.items.length ? (
                <TasksTable
                  canReorder={result.permissions.canReorder}
                  reorderDisabledReason={reorderDisabledReason}
                  revision={result.taskOrderRevision}
                  onReorder={onReorder}
                  tasks={result.items}
                  resolvedItems={result.resolvedItems}
                  projectId={projectId}
                  statusPending={statusPending}
                  onStatusChange={onStatusChange}
                  onArchive={onArchive}
                  sections={stages.length ? getStageTableSections(projectId, stages, result.items) : undefined}
                />
              ) : (
                <p className="text-neutral-500 py-8">No {TERMINOLOGY.task.lowerPlural} found.</p>
              )
            }
          />
        </div>
      </CustomScrollbar>
      <ConfirmDialog state={archiveDialogState} setState={setArchiveDialogState} />
      <StageDialog projectId={projectId} />
      <TaskFormDialog
        isOpen={isCreateOpen}
        setIsOpen={setIsCreateOpen}
        project={project}
        rootItemId={rootItemId}
        onSave={async (values) => {
          await createTask.mutateAsync(values);
        }}
      />
    </div>
  );
};
export default ProjectTasks;
